import { zodResolver } from '@hookform/resolvers/zod';
import { type Tenant, type UpdateTenant, UpdateTenantSchema } from '@servicetim/shared';
import { useQueryKeys, useTenant } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConflictDialog,
  Input,
  ProblemBanner,
  QueryState,
  toast,
  useConflictHandler,
} from '@servicetim/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { useUpdateTenantIfMatch } from '../api';
import { FormField, fieldA11y } from '../common/form-field';
import { TimeZoneInput } from '../common/time-zone-input';
import { useFormSubmit } from '../common/use-form-submit';
import { BrandingPreview } from './branding-preview';

type FormInput = z.input<typeof UpdateTenantSchema>;

const DEFAULT_COLOR = '#2554eb';

/** Loads the tenant, then renders the settings form + branding preview. */
export function TenantSettings() {
  const tenant = useTenant();
  return (
    <QueryState {...tenant} onRetry={() => void tenant.refetch()}>
      {(data) => <TenantSettingsForm key={data.configVersion} tenant={data} />}
    </QueryState>
  );
}

const emptyToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;
const emptyToNull = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? null : value;

function TenantSettingsForm({ tenant }: { tenant: Tenant }) {
  const id = useId();
  const keys = useQueryKeys();
  const queryClient = useQueryClient();
  const update = useUpdateTenantIfMatch();
  const tenantQuery = useTenant();
  const branding = tenant.settings.branding ?? {};

  const form = useForm<FormInput, unknown, UpdateTenant>({
    resolver: zodResolver(UpdateTenantSchema),
    defaultValues: {
      name: tenant.name,
      settings: {
        defaultTimezone: tenant.settings.defaultTimezone ?? 'UTC',
        branding: {
          productName: branding.productName ?? '',
          portalName: branding.portalName ?? '',
          logoUrl: branding.logoUrl ?? null,
          primaryColor: branding.primaryColor ?? DEFAULT_COLOR,
        },
      },
    },
  });
  const submit = useFormSubmit(form);
  const conflict = useConflictHandler(() => tenantQuery.refetch());
  const { errors } = form.formState;
  const watched = form.watch();

  const onSubmit = form.handleSubmit(async (patch) => {
    const saved = await submit.run(() =>
      conflict.guard(() => {
        // Read the latest version inside the closure so "reload and retry" sends the new one.
        const latest = queryClient.getQueryData<Tenant>(keys.tenant);
        return update.mutateAsync({
          patch,
          ifMatch: latest?.configVersion ?? tenant.configVersion,
        });
      }),
    );
    if (saved) {
      toast.success('Tenant settings saved');
      form.reset(form.getValues());
    }
  });

  const brandingErrors = errors.settings?.branding;
  const colorId = `${id}-color`;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <form onSubmit={onSubmit} noValidate className="space-y-6">
        {submit.error ? <ProblemBanner error={submit.error} omitPaths={submit.omitPaths} /> : null}

        <Card>
          <CardHeader>
            <CardTitle>General</CardTitle>
            <CardDescription>
              The tenant name appears in the admin app and in every notification.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <FormField label="Name" htmlFor={`${id}-name`} required error={errors.name?.message}>
              <Input
                {...form.register('name')}
                {...fieldA11y(`${id}-name`, { error: errors.name })}
                maxLength={120}
              />
            </FormField>
            <div className="space-y-1.5">
              <p className="font-medium text-fg text-sm leading-none">Plan</p>
              <div className="flex h-9 items-center gap-2">
                <Badge variant="info">{tenant.plan}</Badge>
                <span className="text-fg-muted text-xs">Change plans through support.</span>
              </div>
            </div>
            <FormField
              label="Default time zone"
              htmlFor={`${id}-tz`}
              description="Used for SLA calendars and dates in emails when a user has no time zone."
              error={errors.settings?.defaultTimezone?.message}
            >
              <TimeZoneInput
                {...form.register('settings.defaultTimezone', { setValueAs: emptyToUndefined })}
                {...fieldA11y(`${id}-tz`, {
                  description: true,
                  error: errors.settings?.defaultTimezone,
                })}
              />
            </FormField>
            <div className="space-y-1.5">
              <p className="font-medium text-fg text-sm leading-none">Slug</p>
              <p className="flex h-9 items-center font-mono text-fg-muted text-sm">{tenant.slug}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Branding</CardTitle>
            <CardDescription>
              Product and portal names, logo and primary color. The preview updates as you type.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Product name"
              htmlFor={`${id}-product`}
              description="Shown in the admin app header and in emails."
              error={brandingErrors?.productName?.message}
            >
              <Input
                {...form.register('settings.branding.productName', {
                  setValueAs: emptyToUndefined,
                })}
                {...fieldA11y(`${id}-product`, {
                  description: true,
                  error: brandingErrors?.productName,
                })}
                maxLength={60}
                placeholder={tenant.name}
              />
            </FormField>
            <FormField
              label="Portal name"
              htmlFor={`${id}-portal`}
              description="Help-center title requesters see."
              error={brandingErrors?.portalName?.message}
            >
              <Input
                {...form.register('settings.branding.portalName', { setValueAs: emptyToUndefined })}
                {...fieldA11y(`${id}-portal`, {
                  description: true,
                  error: brandingErrors?.portalName,
                })}
                maxLength={80}
                placeholder={`${tenant.name} help center`}
              />
            </FormField>
            <FormField
              label="Logo URL"
              htmlFor={`${id}-logo`}
              description="HTTPS image URL; leave empty to show the product initial."
              error={brandingErrors?.logoUrl?.message}
            >
              <Input
                type="url"
                {...form.register('settings.branding.logoUrl', { setValueAs: emptyToNull })}
                {...fieldA11y(`${id}-logo`, { description: true, error: brandingErrors?.logoUrl })}
                placeholder="https://"
              />
            </FormField>
            <FormField
              label="Primary color"
              htmlFor={colorId}
              description="Hex color such as #1d4ed8."
              error={brandingErrors?.primaryColor?.message}
            >
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label="Pick primary color"
                  className="size-9 cursor-pointer rounded-md border border-border-strong bg-surface-raised p-1"
                  value={
                    /^#[0-9a-fA-F]{6}$/.test(watched.settings?.branding?.primaryColor ?? '')
                      ? (watched.settings?.branding?.primaryColor as string)
                      : DEFAULT_COLOR
                  }
                  onChange={(event) =>
                    form.setValue('settings.branding.primaryColor', event.target.value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                />
                <Input
                  {...form.register('settings.branding.primaryColor', {
                    setValueAs: emptyToUndefined,
                  })}
                  {...fieldA11y(colorId, {
                    description: true,
                    error: brandingErrors?.primaryColor,
                  })}
                  className="font-mono"
                  maxLength={7}
                  placeholder={DEFAULT_COLOR}
                />
              </div>
            </FormField>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={!form.formState.isDirty || submit.busy}
            onClick={() => form.reset()}
          >
            Discard changes
          </Button>
          <Button type="submit" disabled={!form.formState.isDirty || submit.busy}>
            {submit.busy ? <LoaderCircle className="animate-spin" /> : null}
            Save settings
          </Button>
        </div>
      </form>

      <div className="lg:sticky lg:top-6 lg:self-start">
        <BrandingPreview
          tenantName={watched.name || tenant.name}
          productName={watched.settings?.branding?.productName}
          portalName={watched.settings?.branding?.portalName}
          logoUrl={watched.settings?.branding?.logoUrl}
          primaryColor={watched.settings?.branding?.primaryColor}
        />
      </div>

      <ConflictDialog {...conflict.dialogProps} subject="the tenant settings" />
    </div>
  );
}
